import { useState } from "react";
import { MessageSquare, Send, User, Mail, FileText } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicContact() {
  const { settings } = useOutletContext();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all fields");
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API_URL}/api/messages`, formData);
      toast.success("Message sent! The tournament admin will get back to you.");
      setFormData({ name: "", email: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-extrabold uppercase tracking-tight">
          Contact
        </h1>
        <p className="text-muted-foreground mt-1">
          Get in touch with the tournament organizers
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Contact Form */}
        <div className="bg-card border border-border rounded-xl p-6">
          <h2 className="font-heading text-lg font-bold uppercase mb-6 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-primary" />
            Send a Message
          </h2>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Your Name</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Smith"
                  className="pl-10 bg-background border-border"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email">Email Address</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@example.com"
                  className="pl-10 bg-background border-border"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="message">Message</Label>
              <div className="relative mt-1">
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Your message..."
                  rows={5}
                  className="bg-background border-border resize-none"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={sending}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {sending ? (
                "Sending..."
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Message
                </>
              )}
            </Button>
          </form>
        </div>

        {/* Info */}
        <div className="space-y-6">
          <div className="bg-card border border-border rounded-xl p-6">
            <h2 className="font-heading text-lg font-bold uppercase mb-4">
              {settings?.name || "Tournament Info"}
            </h2>
            <p className="text-muted-foreground mb-4">
              For inquiries about the tournament, team registrations, or any other questions, 
              please use the contact form or reach out to the tournament organizers directly.
            </p>
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-3">
                <FileText className="w-4 h-4 text-primary" />
                <span>Match schedules and results</span>
              </div>
              <div className="flex items-center gap-3">
                <User className="w-4 h-4 text-primary" />
                <span>Player registration inquiries</span>
              </div>
              <div className="flex items-center gap-3">
                <MessageSquare className="w-4 h-4 text-primary" />
                <span>General tournament questions</span>
              </div>
            </div>
          </div>

          <div className="bg-primary/10 border border-primary/30 rounded-xl p-6">
            <h3 className="font-bold mb-2">Response Time</h3>
            <p className="text-sm text-muted-foreground">
              We typically respond to messages within 24-48 hours. 
              For urgent matters, please indicate so in your message.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
